import { Action as AccessTokenAction, ActionType as AccessTokenActionType } from '~/AccessToken';
import { Country } from '~/Api/Country';
import { AeMaxPrice, ProductUpdate, ReferenceUpdate, ValidationResult } from '~/Api/User/ProductGroup/AeMaxPrice';

import { Action, ActionType } from './Action';

export interface AeMaxPriceState {
	request: Promise<any>;
	isFetching: boolean;
	aeMaxPrice: AeMaxPrice | null;
}

type UpdateState =
	| {
			name: 'scheduled';
			timeoutId: NodeJS.Timeout;
			reference: ReferenceUpdate;
			products: ProductUpdate[];
	  }
	| { name: 'fetching'; request: Promise<any> | null }
	| { name: 'updated' }
	| { name: 'failed' };

export interface Update {
	groupId: string;
	state: UpdateState;
}

export interface Export {
	groupId: string;
	request: Promise<any>;
	isFetching: boolean;
	validationResult: ValidationResult | null;
}

export interface State {
	byId: { [key in string]: AeMaxPriceState };
	update: Update | null;
	export: Export | null;
	nationalities: {
		request: Promise<any>;
		isFetching: boolean;
		items: Country[];
	} | null;
}

function createState(): State {
	return {
		byId: {},
		update: null,
		export: null,
		nationalities: null,
	};
}

export default function reducer(state: State = createState(), action: Action | AccessTokenAction): State {
	switch (action.type) {
		case ActionType.request:
			return {
				...state,
				byId: {
					...state.byId,
					[action.groupId]: {
						request: action.request,
						isFetching: true,
						aeMaxPrice: state.byId[action.groupId]?.aeMaxPrice ?? null,
					},
				},
			};

		case ActionType.receive: {
			const aeMaxPrice = state.byId[action.groupId];
			if (aeMaxPrice?.request !== action.request) {
				return state;
			}

			if (!action.aeMaxPrice) {
				const byId = { ...state.byId };
				delete byId[action.groupId];
				return { ...state, byId };
			}

			return {
				...state,
				byId: {
					[action.groupId]: {
						...aeMaxPrice,
						isFetching: false,
						aeMaxPrice: action.aeMaxPrice,
					},
				},
			};
		}

		case ActionType.prune: {
			const byId = { ...state.byId };
			delete byId[action.groupId];
			return { ...state, byId };
		}

		case ActionType.scheduleUpdate: {
			const updateState = state.update?.state;
			if (updateState && updateState.name === 'scheduled') {
				clearTimeout(updateState.timeoutId);
			}

			return {
				...state,
				update: {
					groupId: action.groupId,
					state: {
						name: 'scheduled',
						timeoutId: action.timeoutId,
						reference: action.reference,
						products: action.products,
					},
				},
			};
		}

		case ActionType.requestUpdate: {
			return {
				...state,
				update: {
					groupId: action.groupId,
					state: { name: 'fetching', request: action.request },
				},
			};
		}

		case ActionType.receiveUpdate: {
			const updateState = state.update?.state;
			if (updateState && updateState.name === 'fetching' && updateState?.request !== action.request) {
				return state;
			}

			const byId = { ...state.byId };
			const aeMaxPriceState = byId[action.groupId];

			if (action.aeMaxPrice && aeMaxPriceState) {
				byId[action.groupId] = {
					...aeMaxPriceState,
					aeMaxPrice: action.aeMaxPrice,
				};
			}

			return {
				...state,
				byId,
				update: {
					groupId: action.groupId,
					state: action.aeMaxPrice ? { name: 'updated' } : { name: 'failed' },
				},
				export: null,
			};
		}

		case ActionType.requestXlsCreate:
			return {
				...state,
				export: {
					groupId: action.groupId,
					request: action.request,
					isFetching: true,
					validationResult: null,
				},
			};

		case ActionType.receiveXlsCreate:
			if (state.export?.request !== action.request) {
				return state;
			}

			return {
				...state,
				export: {
					...state.export,
					isFetching: false,
					validationResult: action.validationResult,
				},
			};

		case ActionType.requestNationalities:
			return {
				...state,
				nationalities: {
					request: action.request,
					isFetching: true,
					items: [],
				},
			};

		case ActionType.receiveNationalities:
			if (action.request !== state.nationalities?.request) {
				return state;
			}
			if (!action.items) {
				return { ...state, nationalities: null };
			}

			return {
				...state,
				nationalities: {
					...state.nationalities,
					isFetching: false,
					items: action.items,
				},
			};

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
}

export function selectAeMaxPrice(state: State, groupId: string): AeMaxPriceState | null {
	return state.byId[groupId];
}

export function selectExport(state: State, groupId: string): Export | null {
	return state.export?.groupId === groupId ? state.export : null;
}

export function selectNationalityByCode(state: State, code: string | null): Country | null {
	return state.nationalities?.items.find((country) => country.alpha2 === code) || null;
}
